/**
 * Which language the assistant should answer in.
 *
 * The assistant does **not** infer a language from the question — it answers in the locale the
 * request carries, falling back to `en` (contract §4.8). So something has to choose one. The app's
 * own i18next language cannot: `supportedLngs` is `["en"]` and the German resources are commented
 * out (`src/i18n/index.ts`), so it resolves to `en` for everyone and German answers would be
 * unreachable. The browser's language is the only signal available that actually varies, and it is
 * the one the user already set to say what they read comfortably.
 *
 * Since PREC-4067 this is only the fallback: each turn answers in the language the user writes in
 * (`chooseAnswerLanguage`, `messageLanguage.ts`), and the browser decides only a first message too
 * short to classify — a German browser is the norm for DACH users who work in English.
 */

/** The locales the help assistant serves; anything else answers in English. */
const ASSISTANT_LOCALES = ["en", "de"];

const DEFAULT_LOCALE = "en";

/** Narrow a browser language tag ("de-AT", "en-US", "fr") to a locale the assistant serves. */
export const helpAnswerLanguage = (browserLanguage?: string | null): string => {
	const base = browserLanguage?.trim().toLowerCase().split("-")[0] ?? "";

	return ASSISTANT_LOCALES.includes(base) ? base : DEFAULT_LOCALE;
};
